import { useQuery } from "@apollo/client";
import type { Conversation } from "@casey/db-types";
import {
  parseConversationMessages,
  formatTranscriptMessages,
  type TranscriptMessage,
} from "@casey/utils/transcript-parser";
import { GET_SESSION_TRANSCRIPT } from "../gql";

// TranscriptMessage is now imported from @casey/utils

interface SessionInfo {
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  participantName: string | null;
  participantEmail: string | null;
  projectName: string;
  templateName: string;
}

interface TranscriptData {
  conversationId: string;
  messages: TranscriptMessage[];
  sessionInfo: SessionInfo;
  sessionContext?: any; // The parsed session context from the database
}

interface UseSessionTranscriptResult {
  transcript: TranscriptData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface SessionTranscriptData {
  conversation: Conversation;
}

interface SessionTranscriptVariables {
  conversationId: string;
}

export function useSessionTranscript(
  conversationId: string | null,
): UseSessionTranscriptResult {
  const { data, loading, error, refetch } = useQuery<
    SessionTranscriptData,
    SessionTranscriptVariables
  >(GET_SESSION_TRANSCRIPT, {
    variables: { conversationId: conversationId! },
    skip: !conversationId,
    errorPolicy: "all",
  });

  const transcript: TranscriptData | null = data?.conversation
    ? {
        conversationId: data.conversation.id,
        messages: parseConversationMessages(data.conversation.conversation),
        sessionInfo: {
          status: data.conversation.status || "unknown",
          startedAt: data.conversation.startedAt || null,
          completedAt: data.conversation.completedAt || null,
          participantName:
            data.conversation.connectionByConnectionCode?.targetName || null,
          participantEmail:
            data.conversation.connectionByConnectionCode?.targetEmail || null,
          projectName: data.conversation.project?.name || "Unknown Project",
          templateName: data.conversation.agent?.name || "Unknown Template",
        },
        sessionContext: (() => {
          if (!data.conversation.context) return null;

          try {
            // GraphQL returns JSONB as a JSON string, so we need to parse it
            return typeof data.conversation.context === "string"
              ? JSON.parse(data.conversation.context)
              : data.conversation.context;
          } catch (error) {
            console.error("Failed to parse session context:", error);
            return null;
          }
        })(),
      }
    : null;

  return {
    transcript,
    loading,
    error: error?.message || null,
    refetch: async () => {
      await refetch();
    },
  };
}

// parseConversationMessages and formatTranscriptMessages are now imported from @workspace/utils

export type { TranscriptMessage, SessionInfo, TranscriptData };
